import React, { useContext, useEffect, useState } from 'react';
import { Route, useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { Alert, Box, Container, Flashbar, Header, Link, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';

import { useMetrics } from 'appSelectorContext';
import { isEmpty } from 'lodash';

import InventoryAdjustment from '../components/InventoryAdjustment';
import Layout from '../components/Layout';
import MPNsToReceive from '../components/MPNsToReceive';
import { useGetPermissions } from '../components/Permissions/usePermissions';
import StartReceiving from '../components/StartReceiving';
import { RECEIVE_TYPES_UI } from '../constants';
import { getSiteDeprecationStatus } from '../constants/siteDeprecations';
import useLocalStorage, { showEvictWarning } from '../hooks/useLocalStorage';
import { Context } from '../store';
import { ReceiveReferenceIdType, ReceiveType, ReferenceQueryResponseItem, Site, URIParams } from '../store/types';
import { ContainerPage } from './Container';
import { WidgetType } from "../widgets/widgetCommon/widgetConstants";

export default function ReceivePage() {
  const { state } = useContext(Context);
  const metrics = useMetrics();
  const publisher = metrics.initialMetricsPublisher.newChildActionPublisherForMethod('ReceiveHomePage');

  const [modelId, setModelId] = useState<string | null>(null);
  const loading = state?.receiveReference?.loading;
  const existingDraft: ReferenceQueryResponseItem =
    state?.receiveReference?.data?.receiveReferenceQueryResponseList?.[0];
  const { path } = useRouteMatch();
  const urlParam = useParams<URIParams>();
  const history = useHistory();
  const [notificationItems, setNotificationItems] = useState<any>([]);
  const [preferredSite, setPreferredSite] = useLocalStorage('unifiedReceivePreferredSite');

  const urlParams = new URLSearchParams(window.location.search);
  const mpn = urlParams.get('mpn');
  const { getPermissionsForIA, iaLoading, getPermissionsForReceive, receiveLoading } = useGetPermissions();
  const isAdjustment =
      urlParam.receiveType === ReceiveType.INVENTORY_ADJUSTMENT ||
      state.widgetType === WidgetType.INVENTORY_ADJUSTMENT;

  const hasPermissionsForIA = state?.hasPermissionsToAccessIA;
  const hasPermissionsForReceive = state?.hasPermissionsToAccessReceive;
  const isLoading = (isAdjustment && iaLoading) || (!isAdjustment && receiveLoading);
  const hasPermission = (isAdjustment && hasPermissionsForIA) || (!isAdjustment && hasPermissionsForReceive);
  const existingDraftIA = existingDraft?.scannableIdFoundAs === ReceiveReferenceIdType.INVENTORY_ADJUSTMENT;
  const selectedDraft = !isAdjustment && !!existingDraftIA ? undefined : existingDraft;
  const draftReceiveReferenceId = !isAdjustment ? selectedDraft?.receiveReference?.receiveReferenceId : undefined;
  const requestReferenceId = urlParam.referenceId || draftReceiveReferenceId;
  const rma = urlParam.receiveType === 'no_reference';
  const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
  const reference = references.find(
    (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === requestReferenceId,
  );

  const referenceId = reference?.receiveReference?.receiveReferenceId;

  useEffect(() => {
    if (!reference && !isEmpty(urlParam.referenceId) && !draftReceiveReferenceId && !rma && !isAdjustment) {
      publisher.publishString('wrongReferenceID', urlParam.referenceId);
      history.push(`/receive/`);
      return;
    }

    if (reference && isEmpty(urlParam.referenceId) && !rma && !isAdjustment) {
      publisher.publishString('draftReceiveReferenceId', draftReceiveReferenceId || '');
      history.push(`/receive/${RECEIVE_TYPES_UI[reference.scannableIdFoundAs]}/${draftReceiveReferenceId}`);
      return;
    }

    if (
      reference &&
      isEmpty(draftReceiveReferenceId) &&
      urlParam.receiveType !== RECEIVE_TYPES_UI[reference.scannableIdFoundAs] &&
      RECEIVE_TYPES_UI[reference.scannableIdFoundAs] &&
      !rma &&
      !isAdjustment
    ) {
      history.push(
        `/receive/${RECEIVE_TYPES_UI[reference.scannableIdFoundAs]}/${reference.receiveReference.receiveReferenceId}`,
      );
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, urlParam, history, draftReceiveReferenceId, publisher, rma, isAdjustment]);

  useEffect(() => {
    setNotificationItems([]);
    // calls to check for IA & Receive permissions
    isAdjustment && getPermissionsForIA();
    getPermissionsForReceive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHandleSiteChange = (option: Site) => {
    setPreferredSite(option);
    setNotificationItems(
      notificationItems.concat({
        type: 'success',
        content: `Your preferred site has been updated to ${option?.value}.`,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setNotificationItems([]),
        id: 'site_changed_message',
      }),
    );
  };

  const title = isAdjustment ? 'Inventory Adjustment' : 'Receive';
  const description = isAdjustment
    ? 'This channel of receive should only be used for Inventory adjustments'
    : 'Receive inbound shipments through a single, guided workflow.';

  const deprecation = getSiteDeprecationStatus(preferredSite?.value);
  const isSiteBlocked = deprecation.status === 'blocked';

  return (
    <Layout description={description} title={title} onSiteChange={onHandleSiteChange}>
      <Flashbar items={notificationItems} />
      <SpaceBetween size="m">
        {showEvictWarning() && (
          <Alert statusIconAriaLabel="Warning" type="warning">
            There are pending shipments that needs to be received on the browser, please receive those shipements or you
            will loose all the pending serials and need to rescan all the parts again
          </Alert>
        )}
        {isSiteBlocked ? (
          <></>
        ) : isLoading ? (
          <Container header={<Header variant="h2"> Checking for access </Header>}>
            <Spinner></Spinner>
          </Container>
        ) : (
          <div>
            {hasPermission ? (
              <SpaceBetween size={'s'} direction="vertical">
                {isAdjustment ? (
                  <InventoryAdjustment preferredSite={preferredSite} />
                ) : (
                  <StartReceiving shipmentIdQuery={state?.initialShipmentReferenceId} />
                )}
                {loading && (
                  <Box margin="xxl" padding="xxl" textAlign="center">
                    <Spinner size="big" />
                  </Box>
                )}

                {!isAdjustment &&
                  (referenceId || (mpn && urlParam?.referenceId)) &&
                  reference &&
                  !loading &&
                  !modelId && (
                    <MPNsToReceive
                      selectedSite={reference?.receiveReference?.siteName}
                      notificationItems={notificationItems}
                      setNotificationItems={setNotificationItems}
                    />
                  )}
                {isAdjustment &&
                  reference?.receiveReference?.reasonCode &&
                  reference?.receiveReference?.expectedReceiveLocation &&
                  !modelId && (
                    <MPNsToReceive
                      receiveReason={reference?.receiveReference?.reasonCode}
                      selectedSite={reference?.receiveReference?.siteName}
                      notificationItems={notificationItems}
                      setNotificationItems={setNotificationItems}
                    />
                  )}

                <Route path={`${path}/model/:modelId`} render={() => <ContainerPage setModelId={setModelId} />} />
              </SpaceBetween>
            ) : (
              <Alert statusIconAriaLabel="Error" type="error" header="Access Denied">
                You do not have access to this tool.{' '}
                <Link href="https://quip-amazon.com/XmNNAlfsjWLf/Unified-Receive-Permissions-Request-Guide">
                  Request permission here.
                </Link>
              </Alert>
            )}
          </div>
        )}
      </SpaceBetween>
    </Layout>
  );
}
